import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setMessage('Passwords do not match.'); return; }
    if (pw.length < 8) { setMessage('Password must be at least 8 characters.'); return; }
    setSaving(true);
    setMessage('');
    try {
      await axios.post(`${API}/api/admin/security/reset-password`, { token, new_password: pw });
      setOk(true);
      setMessage('Password reset. Redirecting to login…');
      setTimeout(() => navigate('/admin/login'), 1500);
    } catch (err) {
      setMessage(err.response?.data?.detail || err.message || 'Reset failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md" data-testid="admin-reset-page">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-cyan-500 rounded-full mx-auto flex items-center justify-center">
              <KeyRound className="text-white" size={22} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-4">Set a new password</h1>
            <p className="text-sm text-slate-500 mt-1">Minimum 8 characters. You'll be signed out of any other devices.</p>
          </div>

          {!token && (
            <p className="text-sm text-red-600" data-testid="admin-reset-missing-token">
              No reset token in URL. Please open the link from your email again.
            </p>
          )}

          {token && !ok && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="rp-1" className="text-slate-900 font-medium">New password</Label>
                <Input
                  id="rp-1"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  minLength={8}
                  className="mt-2"
                  data-testid="admin-reset-pw1"
                />
              </div>
              <div>
                <Label htmlFor="rp-2" className="text-slate-900 font-medium">Confirm new password</Label>
                <Input
                  id="rp-2"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  minLength={8}
                  className="mt-2"
                  data-testid="admin-reset-pw2"
                />
              </div>
              {message && <p className="text-sm text-red-600">{message}</p>}
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-5 rounded-lg"
                data-testid="admin-reset-submit"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {saving ? 'Saving…' : 'Set new password'}
              </Button>
            </form>
          )}

          {ok && <p className="text-sm text-green-700" data-testid="admin-reset-success">{message}</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPassword;
